import React, { useState, useEffect } from 'react';
import {
    Container, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Typography,
    Box, TextField, InputAdornment, Chip, Dialog,
    DialogTitle, DialogContent, DialogActions, Button,
    Grid, IconButton, FormControlLabel, Switch, Alert,
    CircularProgress, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import {
    Search as SearchIcon,
    ContentCopy as ContentCopyIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Upload as UploadIcon
} from '@mui/icons-material';
import api from '../services/api';

function BkpDuplicados() {
    const [registros, setRegistros] = useState([]);
    const [search, setSearch] = useState('');
    const [mostrarInativos, setMostrarInativos] = useState(false);
    const [selectedRegistro, setSelectedRegistro] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [migrarDialogOpen, setMigrarDialogOpen] = useState(false);
    const [escolherProcessoDialogOpen, setEscolherProcessoDialogOpen] = useState(false);
    const [processosDisponiveis, setProcessosDisponiveis] = useState([]);
    const [processoSelecionado, setProcessoSelecionado] = useState('');
    const [migrando, setMigrando] = useState(false);
    const [stats, setStats] = useState(null);

    useEffect(() => {
        loadRegistros();
        loadStats();
    }, [mostrarInativos]);

    const loadRegistros = async () => {
        try {
            const params = mostrarInativos ? '?mostrar_inativos=true' : '';
            const response = await api.get(`/bkp-duplicados${params}`);
            setRegistros(response.data);
        } catch (error) {
            console.error('Erro ao carregar registros duplicados:', error);
        }
    };

    const loadStats = async () => {
        try {
            const response = await api.get('/bkp-duplicados/stats/summary');
            setStats(response.data);
        } catch (error) {
            console.error('Erro ao carregar estatísticas:', error);
        }
    };

    const handleViewDetails = async (registro) => {
        try {
            // Carrega o registro completo (todas as colunas da tabela)
            const response = await api.get(`/bkp-duplicados/${registro.id}`);
            setSelectedRegistro(response.data);
            setDialogOpen(true);
        } catch (error) {
            console.error('Erro ao carregar detalhes do registro duplicado:', error);
            // Fallback: mostra ao menos o que já temos em memória
            setSelectedRegistro(registro);
            setDialogOpen(true);
        }
    };

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
        alert('Copiado para a área de transferência!');
    };

    const handleMigrar = async (registro) => {
        setSelectedRegistro(registro);
        
        // Se não tem processo, precisa escolher um processo existente
        if (!registro.processo) {
            await carregarProcessosPorCliente(registro.nome);
            setEscolherProcessoDialogOpen(true);
        } else {
            setMigrarDialogOpen(true);
        }
    };

    const carregarProcessosPorCliente = async (nomeCliente) => {
        try {
            const response = await api.get(`/bkp-duplicados/processos-por-cliente/${encodeURIComponent(nomeCliente)}`);
            setProcessosDisponiveis(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar processos:', error);
            setProcessosDisponiveis([]);
        }
    };

    const confirmarMigracao = async () => {
        if (!selectedRegistro) return;
        
        setMigrando(true);
        try {
            const body = selectedRegistro.processo ? {} : { processo_id_existente: processoSelecionado };
            const response = await api.post(`/bkp-duplicados/${selectedRegistro.id}/migrar`, body);
            
            alert('Registro migrado com sucesso!');
            setMigrarDialogOpen(false);
            setEscolherProcessoDialogOpen(false);
            setSelectedRegistro(null);
            setProcessoSelecionado('');
            loadRegistros();
            loadStats();
        } catch (error) {
            console.error('Erro ao migrar registro:', error);
            const errorMsg = error.response?.data?.error || 'Erro ao migrar registro';
            
            if (error.response?.data?.requer_selecao) {
                // Se requer seleção de processo, abre o dialog de escolha
                await carregarProcessosPorCliente(selectedRegistro.nome);
                setMigrarDialogOpen(false);
                setEscolherProcessoDialogOpen(true);
            } else {
                alert('Erro: ' + errorMsg);
            }
        } finally {
            setMigrando(false);
        }
    };

    const formatDate = (date) => {
        if (!date) return '-';
        try {
            return new Date(date).toLocaleDateString('pt-BR');
        } catch {
            return date;
        }
    };

    const registrosFiltrados = registros.filter(r => {
        if (!search) return true;
        const searchLower = search.toLowerCase();
        return (
            (r.nome && r.nome.toLowerCase().includes(searchLower)) ||
            (r.cpf && r.cpf.toLowerCase().includes(searchLower)) ||
            (r.processo && r.processo.toLowerCase().includes(searchLower)) ||
            (r.motivo && r.motivo.toLowerCase().includes(searchLower))
        );
    }).sort((a, b) => {
        // Ordena por linha_planilha (menor primeiro)
        const linhaA = a.linha_planilha || 999999;
        const linhaB = b.linha_planilha || 999999;
        return linhaA - linhaB;
    });

    return (
        <Container>
            {/* Header destacado para indicar a tela ativa */}
            <Box 
                sx={{ 
                    mb: 3, 
                    p: 2, 
                    backgroundColor: 'primary.main', 
                    color: 'primary.contrastText',
                    borderRadius: 2,
                    boxShadow: 2
                }}
            >
                <Typography 
                    variant="h4" 
                    component="h1" 
                    sx={{ 
                        fontSize: { xs: '1.5rem', sm: '2rem' },
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <WarningIcon sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                    Registros Duplicados
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                    Esta é uma tela de consulta. Use os dados para atualizar manualmente os registros de processos.
                </Typography>
            </Box>

            {stats && (
                <Box sx={{ mb: 3 }}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>Estatísticas</Typography>
                        <Typography variant="body1">Total de registros: <strong>{stats.total}</strong></Typography>
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>Por motivo:</Typography>
                            {stats.por_motivo.map((item, index) => (
                                <Chip 
                                    key={index}
                                    label={`${item.motivo}: ${item.total}`}
                                    sx={{ mr: 1, mb: 1 }}
                                    size="small"
                                />
                            ))}
                        </Box>
                    </Paper>
                </Box>
            )}

            <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                    fullWidth
                    placeholder="Buscar por nome, CPF, processo ou motivo..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ flex: 1, minWidth: 200 }}
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={mostrarInativos}
                            onChange={(e) => setMostrarInativos(e.target.checked)}
                        />
                    }
                    label="Mostrar inativos/migrados"
                />
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Linha</TableCell>
                            <TableCell>Nome</TableCell>
                            <TableCell>CPF</TableCell>
                            <TableCell>Processo</TableCell>
                            <TableCell>Motivo</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Data</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {registrosFiltrados.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    {search ? 'Nenhum registro encontrado' : 'Nenhum registro duplicado'}
                                </TableCell>
                            </TableRow>
                        ) : (
                            registrosFiltrados.map((registro) => (
                                <TableRow 
                                    key={registro.id} 
                                    hover
                                    sx={{ 
                                        cursor: 'pointer',
                                        opacity: registro.migrado === 1 ? 0.6 : 1,
                                        backgroundColor: registro.ativo === 0 ? 'action.hover' : 'inherit'
                                    }}
                                    onClick={() => handleViewDetails(registro)}
                                >
                                    <TableCell>{registro.linha_planilha || '-'}</TableCell>
                                    <TableCell>{registro.nome || '-'}</TableCell>
                                    <TableCell>{registro.cpf || '-'}</TableCell>
                                    <TableCell>{registro.processo || '-'}</TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={registro.motivo || 'Sem motivo'} 
                                            size="small"
                                            color="warning"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            {registro.migrado === 1 ? (
                                                <Chip 
                                                    icon={<CheckCircleIcon />}
                                                    label="Migrado" 
                                                    size="small"
                                                    color="success"
                                                />
                                            ) : (
                                                <Chip 
                                                    icon={<CancelIcon />}
                                                    label="Pendente" 
                                                    size="small"
                                                    color="default"
                                                />
                                            )}
                                            {registro.ativo === 0 && (
                                                <Chip 
                                                    label="Inativo" 
                                                    size="small"
                                                    color="default"
                                                    variant="outlined"
                                                />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{formatDate(registro.data_distribuicao)}</TableCell>
                                    <TableCell align="right">
                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                            {registro.migrado !== 1 && (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    color="primary"
                                                    startIcon={<UploadIcon />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleMigrar(registro);
                                                    }}
                                                >
                                                    Migrar
                                                </Button>
                                            )}
                                            <Button
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleViewDetails(registro);
                                                }}
                                            >
                                                Ver Detalhes
                                            </Button>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Dialog de Detalhes */}
            <Dialog 
                open={dialogOpen} 
                onClose={() => setDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Detalhes do Registro Duplicado
                </DialogTitle>
                <DialogContent>
                    {selectedRegistro && (
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="text.secondary">Nome</Typography>
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    {selectedRegistro.nome || '-'}
                                    <IconButton 
                                        size="small" 
                                        onClick={() => handleCopy(selectedRegistro.nome)}
                                        sx={{ ml: 1 }}
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="text.secondary">CPF</Typography>
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    {selectedRegistro.cpf || '-'}
                                    <IconButton 
                                        size="small" 
                                        onClick={() => handleCopy(selectedRegistro.cpf)}
                                        sx={{ ml: 1 }}
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="text.secondary">Processo</Typography>
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    {selectedRegistro.processo || '-'}
                                    <IconButton 
                                        size="small" 
                                        onClick={() => handleCopy(selectedRegistro.processo)}
                                        sx={{ ml: 1 }}
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="text.secondary">Motivo</Typography>
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    {selectedRegistro.motivo || '-'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="text.secondary">Histórico Geral do Processo</Typography>
                                <Paper sx={{ p: 2, mt: 1, mb: 2, bgcolor: 'grey.50', maxHeight: 200, overflow: 'auto' }}>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {selectedRegistro.historico_geral || 'Sem histórico'}
                                    </Typography>
                                </Paper>
                                <Button
                                    size="small"
                                    startIcon={<ContentCopyIcon />}
                                    onClick={() => handleCopy(selectedRegistro.historico_geral || '')}
                                >
                                    Copiar Histórico
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="text.secondary">Linha da Planilha</Typography>
                                <Typography variant="body1">{selectedRegistro.linha_planilha || '-'}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="text.secondary">Data de Criação</Typography>
                                <Typography variant="body1">{formatDate(selectedRegistro.created_at)}</Typography>
                            </Grid>
                            {/* Bloco de dados adicionais do cliente */}
                            {(selectedRegistro.endereco ||
                              selectedRegistro.bairro_distrito ||
                              selectedRegistro.cidade ||
                              selectedRegistro.uf ||
                              selectedRegistro.fone1 ||
                              selectedRegistro.fone2 ||
                              selectedRegistro.nascimento ||
                              selectedRegistro.idade ||
                              selectedRegistro.sexo ||
                              selectedRegistro.pis_pasep ||
                              selectedRegistro.lembrete ||
                              selectedRegistro.data_obito2 ||
                              selectedRegistro.senha) && (
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="text.secondary">Dados do Cliente (backup da planilha)</Typography>
                                    <Paper sx={{ p: 2, mt: 1, mb: 2, bgcolor: 'grey.50' }}>
                                        <Grid container spacing={2}>
                                            {selectedRegistro.endereco && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2"><strong>Endereço:</strong> {selectedRegistro.endereco}</Typography>
                                                </Grid>
                                            )}
                                            {(selectedRegistro.bairro_distrito || selectedRegistro.cidade || selectedRegistro.uf) && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2">
                                                        <strong>Bairro/Cidade/UF:</strong>{' '}
                                                        {[selectedRegistro.bairro_distrito, selectedRegistro.cidade, selectedRegistro.uf].filter(Boolean).join(' / ') || '-'}
                                                    </Typography>
                                                </Grid>
                                            )}
                                            {(selectedRegistro.fone1 || selectedRegistro.fone2) && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2">
                                                        <strong>Telefones:</strong>{' '}
                                                        {[selectedRegistro.fone1, selectedRegistro.fone2].filter(Boolean).join(' / ') || '-'}
                                                    </Typography>
                                                </Grid>
                                            )}
                                            {(selectedRegistro.nascimento || selectedRegistro.idade) && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2">
                                                        <strong>Nascimento / Idade:</strong>{' '}
                                                        {[formatDate(selectedRegistro.nascimento), selectedRegistro.idade && `${selectedRegistro.idade} anos`]
                                                            .filter(Boolean)
                                                            .join(' - ') || '-'}
                                                    </Typography>
                                                </Grid>
                                            )}
                                            {selectedRegistro.sexo && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2"><strong>Sexo:</strong> {selectedRegistro.sexo}</Typography>
                                                </Grid>
                                            )}
                                            {selectedRegistro.pis_pasep && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2"><strong>PIS/PASEP:</strong> {selectedRegistro.pis_pasep}</Typography>
                                                </Grid>
                                            )}
                                            {selectedRegistro.lembrete && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2"><strong>Lembrete:</strong> {selectedRegistro.lembrete}</Typography>
                                                </Grid>
                                            )}
                                            {selectedRegistro.data_obito2 && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2"><strong>Data Óbito:</strong> {formatDate(selectedRegistro.data_obito2)}</Typography>
                                                </Grid>
                                            )}
                                            {selectedRegistro.senha && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2"><strong>Senha INSS (backup):</strong> {selectedRegistro.senha}</Typography>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Paper>
                                </Grid>
                            )}
                            {/* Bloco de dados adicionais do processo */}
                            {(selectedRegistro.tipo_beneficio ||
                              selectedRegistro.status_processo ||
                              selectedRegistro.tempo_contribuicao ||
                              selectedRegistro.vara ||
                              selectedRegistro.comarca ||
                              selectedRegistro.localizacao ||
                              selectedRegistro.localizacao_pasta ||
                              selectedRegistro.data_distribuicao ||
                              selectedRegistro.data_ajuizamento ||
                              selectedRegistro.data_processo ||
                              selectedRegistro.data_implantacao ||
                              selectedRegistro.data4 ||
                              selectedRegistro.data5 ||
                              selectedRegistro.visto ||
                              selectedRegistro.filtro ||
                              selectedRegistro.area ||
                              selectedRegistro.distribuicao ||
                              selectedRegistro.materia ||
                              selectedRegistro.situacao ||
                              selectedRegistro.data_parecer ||
                              selectedRegistro.qual_peca ||
                              selectedRegistro.data_prazo ||
                              selectedRegistro.partes_externas ||
                              selectedRegistro.advogado) && (
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="text.secondary">Dados do Processo (backup da planilha)</Typography>
                                    <Paper sx={{ p: 2, mt: 1, mb: 2, bgcolor: 'grey.50' }}>
                                        <Grid container spacing={2}>
                                            {selectedRegistro.tipo_beneficio && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2"><strong>Tipo de Benefício:</strong> {selectedRegistro.tipo_beneficio}</Typography>
                                                </Grid>
                                            )}
                                            {selectedRegistro.status_processo && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2"><strong>Status do Processo:</strong> {selectedRegistro.status_processo}</Typography>
                                                </Grid>
                                            )}
                                            {selectedRegistro.tempo_contribuicao && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2"><strong>Tempo de Contribuição:</strong> {selectedRegistro.tempo_contribuicao}</Typography>
                                                </Grid>
                                            )}
                                            {(selectedRegistro.vara || selectedRegistro.comarca) && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2">
                                                        <strong>Vara / Comarca:</strong>{' '}
                                                        {[selectedRegistro.vara, selectedRegistro.comarca].filter(Boolean).join(' - ') || '-'}
                                                    </Typography>
                                                </Grid>
                                            )}
                                            {(selectedRegistro.localizacao || selectedRegistro.localizacao_pasta) && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2">
                                                        <strong>Localização / Pasta:</strong>{' '}
                                                        {[selectedRegistro.localizacao, selectedRegistro.localizacao_pasta].filter(Boolean).join(' - ') || '-'}
                                                    </Typography>
                                                </Grid>
                                            )}
                                            {(selectedRegistro.data_distribuicao ||
                                              selectedRegistro.data_ajuizamento ||
                                              selectedRegistro.data_processo ||
                                              selectedRegistro.data_implantacao) && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2">
                                                        <strong>Datas:</strong>{' '}
                                                        {[
                                                            selectedRegistro.data_distribuicao && `Distrib.: ${formatDate(selectedRegistro.data_distribuicao)}`,
                                                            selectedRegistro.data_ajuizamento && `Ajuiz.: ${formatDate(selectedRegistro.data_ajuizamento)}`,
                                                            selectedRegistro.data_processo && `Proc.: ${formatDate(selectedRegistro.data_processo)}`,
                                                            selectedRegistro.data_implantacao && `Implant.: ${formatDate(selectedRegistro.data_implantacao)}`
                                                        ].filter(Boolean).join(' | ') || '-'}
                                                    </Typography>
                                                </Grid>
                                            )}
                                            {(selectedRegistro.data4 || selectedRegistro.data5) && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2">
                                                        <strong>Outras datas:</strong>{' '}
                                                        {[
                                                            selectedRegistro.data4 && `Data4: ${formatDate(selectedRegistro.data4)}`,
                                                            selectedRegistro.data5 && `Data5: ${formatDate(selectedRegistro.data5)}`
                                                        ].filter(Boolean).join(' | ') || '-'}
                                                    </Typography>
                                                </Grid>
                                            )}
                                            {(selectedRegistro.visto || selectedRegistro.filtro) && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2">
                                                        <strong>Visto / Filtro:</strong>{' '}
                                                        {[selectedRegistro.visto, selectedRegistro.filtro].filter(Boolean).join(' - ') || '-'}
                                                    </Typography>
                                                </Grid>
                                            )}
                                            {(selectedRegistro.area || selectedRegistro.distribuicao || selectedRegistro.materia) && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2">
                                                        <strong>Área / Distribuição / Matéria:</strong>{' '}
                                                        {[selectedRegistro.area, selectedRegistro.distribuicao, selectedRegistro.materia].filter(Boolean).join(' - ') || '-'}
                                                    </Typography>
                                                </Grid>
                                            )}
                                            {(selectedRegistro.situacao || selectedRegistro.data_parecer) && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2">
                                                        <strong>Situação / Data Parecer:</strong>{' '}
                                                        {[
                                                            selectedRegistro.situacao,
                                                            selectedRegistro.data_parecer && formatDate(selectedRegistro.data_parecer)
                                                        ].filter(Boolean).join(' - ') || '-'}
                                                    </Typography>
                                                </Grid>
                                            )}
                                            {(selectedRegistro.qual_peca || selectedRegistro.data_prazo) && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2">
                                                        <strong>Peça / Data Prazo:</strong>{' '}
                                                        {[
                                                            selectedRegistro.qual_peca,
                                                            selectedRegistro.data_prazo && formatDate(selectedRegistro.data_prazo)
                                                        ].filter(Boolean).join(' - ') || '-'}
                                                    </Typography>
                                                </Grid>
                                            )}
                                            {(selectedRegistro.partes_externas || selectedRegistro.advogado) && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="body2">
                                                        <strong>Partes Externas / Advogado:</strong>{' '}
                                                        {[selectedRegistro.partes_externas, selectedRegistro.advogado].filter(Boolean).join(' - ') || '-'}
                                                    </Typography>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Paper>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    {selectedRegistro && selectedRegistro.migrado !== 1 && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<UploadIcon />}
                            onClick={() => {
                                setDialogOpen(false);
                                handleMigrar(selectedRegistro);
                            }}
                        >
                            Migrar
                        </Button>
                    )}
                    <Button onClick={() => setDialogOpen(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>

            {/* Dialog de Confirmação de Migração */}
            <Dialog 
                open={migrarDialogOpen} 
                onClose={() => !migrando && setMigrarDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Confirmar Migração</DialogTitle>
                <DialogContent>
                    {selectedRegistro && (
                        <Box>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <Typography variant="body2" gutterBottom>
                                    <strong>Atenção:</strong> Esta ação irá migrar o registro para a tabela de processos.
                                </Typography>
                                <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                                    <strong>Registro:</strong> {selectedRegistro.nome || '-'}<br />
                                    <strong>CPF:</strong> {selectedRegistro.cpf || '-'}<br />
                                    <strong>Processo:</strong> {selectedRegistro.processo || '-'}<br />
                                    <strong>Linha da Planilha:</strong> {selectedRegistro.linha_planilha || '-'}
                                </Typography>
                            </Alert>
                            <Typography variant="body2" color="text.secondary">
                                O sistema irá:
                            </Typography>
                            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                                <li>Verificar se já existe um processo com o mesmo número</li>
                                <li>Se existir, adicionar os andamentos do histórico ao processo existente</li>
                                <li>Se não existir, criar um novo processo</li>
                                <li>Marcar este registro como migrado e inativo</li>
                            </ul>
                            <Alert severity="info" sx={{ mt: 2 }}>
                                <strong>Importante:</strong> Migre sempre na ordem das linhas da planilha (menor linha primeiro).
                            </Alert>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setMigrarDialogOpen(false)} 
                        disabled={migrando}
                    >
                        Cancelar
                    </Button>
                    <Button 
                        onClick={confirmarMigracao} 
                        variant="contained" 
                        color="primary"
                        disabled={migrando}
                        startIcon={migrando ? <CircularProgress size={16} /> : <UploadIcon />}
                    >
                        {migrando ? 'Migrando...' : 'Confirmar Migração'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog de Escolha de Processo (para registros sem processo) */}
            <Dialog 
                open={escolherProcessoDialogOpen} 
                onClose={() => !migrando && setEscolherProcessoDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Escolher Processo para Vincular</DialogTitle>
                <DialogContent>
                    {selectedRegistro && (
                        <Box sx={{ mt: 2 }}>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Este registro não possui número de processo. Selecione um processo existente do cliente <strong>{selectedRegistro.nome}</strong> para vincular os dados.
                            </Alert>
                            {processosDisponiveis.length === 0 ? (
                                <Alert severity="warning">
                                    Nenhum processo encontrado para este cliente. Será necessário criar um processo manualmente primeiro.
                                </Alert>
                            ) : (
                                <FormControl fullWidth>
                                    <InputLabel>Processo</InputLabel>
                                    <Select
                                        value={processoSelecionado}
                                        onChange={(e) => setProcessoSelecionado(e.target.value)}
                                        label="Processo"
                                    >
                                        {processosDisponiveis.map((proc) => (
                                            <MenuItem key={proc.id} value={proc.id}>
                                                {proc.numero_processo} - {proc.status || 'Sem status'} ({formatDate(proc.data_distribuicao)})
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => {
                            setEscolherProcessoDialogOpen(false);
                            setProcessoSelecionado('');
                        }} 
                        disabled={migrando}
                    >
                        Cancelar
                    </Button>
                    <Button 
                        onClick={confirmarMigracao} 
                        variant="contained" 
                        color="primary"
                        disabled={migrando || !processoSelecionado || processosDisponiveis.length === 0}
                        startIcon={migrando ? <CircularProgress size={16} /> : <UploadIcon />}
                    >
                        {migrando ? 'Migrando...' : 'Confirmar Migração'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default BkpDuplicados;

